import { AccountInfo, AuthenticationResult, Configuration, EventMessage, EventType, PublicClientApplication, RedirectRequest, SilentRequest, InteractionRequiredAuthError } from '@azure/msal-browser';
import { appConfig } from '../AppConfig';
import { appInsights } from '../AppInsights';
import i18n from '../i18n';

// standard code to use MSAL with EntraID
const msalConfiguration: Configuration = {
    auth: {
        clientId: appConfig.CLIENT_ID,
        authority: `https://login.microsoftonline.com/${appConfig.TENANT_ID}/v2.0`, // important to put v2.0 to request EntraID v2 token
        redirectUri: appConfig.REDIRECT_URI,
    },
};

// requested scopes are defined in the appConfig
// additional Microsoft Graph API scopes needs to implicitely be defined as optional claims in the EntraID app registration
export const loginRequest: RedirectRequest = {
    //scopes: ['openid', 'profile', 'offline_access', 'email', 'User.Read'].concat(appConfig.MIDDLEWARE_API_SCOPES),
    scopes: appConfig.MIDDLEWARE_API_SCOPES
};

// requested scopes are defined in the appConfig
// additional Microsoft Graph API scopes needs to implicitely be defined as optional claims in the EntraID app registration
export const userRequest: SilentRequest = {
    //scopes: ['openid', 'profile', 'offline_access', 'email', 'User.Read'],
    scopes: appConfig.MIDDLEWARE_API_SCOPES
};

export const publicClientApp = new PublicClientApplication(msalConfiguration);
publicClientApp.initialize();
function setActiveAccount(account: AccountInfo) {
    publicClientApp.setActiveAccount(account);
}
const accounts = publicClientApp.getAllAccounts();
if (accounts.length > 0) {
    setActiveAccount(accounts[0]);
} else {
    // Handle case when no accounts are found
    console.warn('No accounts found during initialization');
}

publicClientApp.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        if (account) {
            setActiveAccount(account);
        }
    } else if (event.eventType === EventType.LOGIN_FAILURE) {
        const error = new Error(`Login failed: ${event.error?.message || 'Unknown error'}`);
        appInsights.trackException({ exception: error });
        console.error('Login failed:', event.error);
        redirectToErrorPage('login_failure', i18n.t('error.api.auth.loginFailure'));
    }
});

export const acquireAccessToken = async (scopes: string[]): Promise<string | undefined> => {
    const account = publicClientApp.getActiveAccount();
    if (!account) {
        const error = new Error('No active account found');
        appInsights.trackException({ exception: error });
        console.error('No active account found');
        redirectToErrorPage('no_account', i18n.t('error.api.auth.noAccount'));
        return undefined;
    }

    try {
        const accessToken = await publicClientApp.acquireTokenSilent({
            scopes,
            account,
        });
        return accessToken.accessToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            // Handle specific MSAL errors - these error codes are standard MSAL error codes
            switch (error.errorCode) {
                case 'interaction_required':
                    // Fallback to interactive login - this will redirect the user
                    console.log('Silent token acquisition failed, redirecting to interactive login');
                    publicClientApp.acquireTokenRedirect({
                        scopes,
                        account,
                    });
                    // No return value as redirect will handle the flow
                    return undefined;
                case 'consent_required':
                    const consentError = new Error(`Consent required for scopes: ${scopes.join(', ')}`);
                    appInsights.trackException({ exception: consentError });
                    console.error('Consent required for scopes:', scopes);
                    redirectToErrorPage('consent_required', i18n.t('error.api.auth.consentRequired'));
                    break;
                case 'claims_challenge':
                    const claimsError = new Error(`Claims challenge required: ${error.message}`);
                    appInsights.trackException({ exception: claimsError });
                    console.error('Claims challenge required:', error);
                    redirectToErrorPage('claims_challenge', i18n.t('error.api.auth.claimsChallenge'));
                    break;
                default:
                    const tokenError = new Error(`Token acquisition error: ${error.errorCode} - ${error.message}`);
                    appInsights.trackException({ exception: tokenError });
                    console.error('Token acquisition error:', error);
                    redirectToErrorPage('token_acquisition_failure', i18n.t('error.api.auth.tokenAcquisitionFailure'));
            }
        } else {
            const unexpectedError = new Error(`Unexpected error during token acquisition: ${(error as Error).message}`);
            appInsights.trackException({ exception: unexpectedError });
            console.error('Unexpected error during token acquisition:', error);
            redirectToErrorPage('unexpected_error', i18n.t('error.api.auth.unexpectedError'));
        }
        return undefined;
    }
};

export function getCurrentAccount(): AccountInfo | null {
    const account = publicClientApp.getActiveAccount();
    if (!account) {
        const warning = new Error('No current account found');
        appInsights.trackException({ exception: warning });
        console.warn('No current account found');
    }
    return account;
}

export async function logout() {
    try {
        await publicClientApp.logoutRedirect();
    } catch (error) {
        const logoutError = new Error(`Logout failed: ${(error as Error).message}`);
        appInsights.trackException({ exception: logoutError });
        console.error('Logout failed:', error);
        redirectToErrorPage('logout_failure', i18n.t('error.api.auth.logoutFailure'));
    }
}

// Helper function to redirect to error page with error details
function redirectToErrorPage(errorCode: string, errorMessage: string) {
    const errorParams = new URLSearchParams({
        errorCode,
        errorMessage,
    });
    window.location.href = `/error?${errorParams.toString()}`;
}

